import { CATALOG_BY_ID } from './catalogue';
import type { CanvasObject, Parcel, Point, TryOnConflict } from './types';

const EPSILON = 1e-7;
export function rotatePoint(point: Point, centre: Point, degrees: number): Point {
  const angle = degrees * Math.PI / 180;
  const x = point.x - centre.x, y = point.y - centre.y;
  return { x: centre.x + x * Math.cos(angle) - y * Math.sin(angle), y: centre.y + x * Math.sin(angle) + y * Math.cos(angle) };
}
export function objectPolygon(object: Pick<CanvasObject, 'x' | 'y' | 'w' | 'h' | 'rotation'> & { kind?: string }): Point[] {
  const centre = { x: object.x + object.w / 2, y: object.y + object.h / 2 };
  if (object.kind && CATALOG_BY_ID[object.kind]?.round) {
    // A circumscribed approximation prevents a curved footprint from receiving a false safe fit.
    const sides = 64;
    const margin = 1 / Math.cos(Math.PI / sides);
    return Array.from({ length: sides }, (_, index) => rotatePoint({ x: centre.x + Math.cos(2 * Math.PI * index / sides) * object.w / 2 * margin, y: centre.y + Math.sin(2 * Math.PI * index / sides) * object.h / 2 * margin }, centre, object.rotation));
  }
  return [
    { x: object.x, y: object.y }, { x: object.x + object.w, y: object.y },
    { x: object.x + object.w, y: object.y + object.h }, { x: object.x, y: object.y + object.h },
  ].map(point => rotatePoint(point, centre, object.rotation));
}
function cross(a: Point, b: Point, c: Point): number { return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x); }
function onSegment(point: Point, a: Point, b: Point): boolean {
  return Math.abs(cross(a, b, point)) < EPSILON && point.x >= Math.min(a.x, b.x) - EPSILON && point.x <= Math.max(a.x, b.x) + EPSILON && point.y >= Math.min(a.y, b.y) - EPSILON && point.y <= Math.max(a.y, b.y) + EPSILON;
}
export function pointInPolygon(point: Point, polygon: readonly Point[], includeBoundary = true): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j]!, b = polygon[i]!;
    if (onSegment(point, a, b)) return includeBoundary;
    if ((a.y > point.y) !== (b.y > point.y) && point.x < (b.x - a.x) * (point.y - a.y) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}
function intersectionParameter(a: Point, b: Point, c: Point, d: Point): number | null {
  const rx = b.x - a.x, ry = b.y - a.y, sx = d.x - c.x, sy = d.y - c.y;
  const divisor = rx * sy - ry * sx;
  if (Math.abs(divisor) < EPSILON) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / divisor;
  const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / divisor;
  return t >= -EPSILON && t <= 1 + EPSILON && u >= -EPSILON && u <= 1 + EPSILON ? Math.max(0, Math.min(1, t)) : null;
}
/** Check edge intervals as well as vertices: four corners inside a concave parcel can still cross outside. */
export function polygonWithin(inner: readonly Point[], outer: readonly Point[]): boolean {
  if (inner.length < 3 || outer.length < 3 || inner.some(point => !pointInPolygon(point, outer))) return false;
  for (let i = 0; i < inner.length; i++) {
    const a = inner[i]!, b = inner[(i + 1) % inner.length]!;
    const cuts = [0, 1];
    for (let j = 0; j < outer.length; j++) {
      const t = intersectionParameter(a, b, outer[j]!, outer[(j + 1) % outer.length]!);
      if (t !== null) cuts.push(t);
    }
    cuts.sort((x, y) => x - y);
    for (let j = 1; j < cuts.length; j++) {
      const t = (cuts[j - 1]! + cuts[j]!) / 2;
      if (!pointInPolygon({ x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) }, outer)) return false;
    }
  }
  return true;
}
export function polygonsOverlap(a: readonly Point[], b: readonly Point[]): boolean {
  if (a.some(point => pointInPolygon(point, b, false)) || b.some(point => pointInPolygon(point, a, false))) return true;
  for (let i = 0; i < a.length; i++) for (let j = 0; j < b.length; j++) {
    const p = a[i]!, q = a[(i + 1) % a.length]!, r = b[j]!, s = b[(j + 1) % b.length]!;
    if (cross(p, q, r) * cross(p, q, s) < -EPSILON && cross(r, s, p) * cross(r, s, q) < -EPSILON) return true;
  }
  // Identical or aligned overlapping rectangles may have every vertex on the other's boundary.
  const centreA = { x: a.reduce((sum, point) => sum + point.x, 0) / a.length, y: a.reduce((sum, point) => sum + point.y, 0) / a.length };
  const centreB = { x: b.reduce((sum, point) => sum + point.x, 0) / b.length, y: b.reduce((sum, point) => sum + point.y, 0) / b.length };
  return pointInPolygon(centreA, b, false) || pointInPolygon(centreB, a, false);
}
export function evaluateTryOn(objects: readonly CanvasObject[], parcel: Parcel, alignment: { x: number; y: number; rotation?: number } = { x: 0, y: 0 }): TryOnConflict[] {
  const transform = (point: Point) => {
    const rotated = rotatePoint(point, { x: 0, y: 0 }, alignment.rotation ?? 0);
    return { x: rotated.x + alignment.x, y: rotated.y + alignment.y };
  };
  const boundary = parcel.poly.map(([x, y]) => transform({ x, y }));
  const conflicts: TryOnConflict[] = parcel.evidence === 'unverified'
    ? [{ type: 'unverified-boundary', message: 'This boundary is unverified. Fit checks are illustrative and do not establish title, setbacks or building permission.' }]
    : [];
  for (const object of objects) {
    if (!CATALOG_BY_ID[object.kind]) continue;
    const footprint = objectPolygon(object);
    if (!polygonWithin(footprint, boundary)) conflicts.push({ type: 'boundary', objectId: object.id, message: `${object.label} crosses the supplied parcel boundary.` });
    for (const feature of parcel.features) {
      const polygon = objectPolygon({ ...feature, rotation: feature.rotation ?? 0 }).map(transform);
      if (polygonsOverlap(footprint, polygon)) conflicts.push({ type: 'existing-feature', objectId: object.id, featureId: feature.id, message: `${object.label} overlaps ${feature.l}.` });
    }
  }
  return conflicts;
}
